import type { tAlertSnapshot } from 'src/alerts/AlertCenter.types';
import { eSubscriptionStatus } from 'src/providers/SubscriptionProvider/Subscription.enums';
import type { tSubscriptionSummary } from 'src/providers/SubscriptionProvider/Subscription.types';
import {
  formatAmountOwed,
  formatPastDueDate,
  resolvePlausibleDate,
  resolveRetryDate,
} from 'src/utils/pastDueDisclosure';

/**
 * U11 (RET-1269) — the past-due slice of `tAlertSnapshot`, assembled here so
 * the Alert Center hook stays a caller and these rules can be tested without
 * mounting sixteen providers.
 *
 * Kept OUT of the hook for a second reason: the invoice-URL guard below is a
 * security boundary, and a security boundary that can only be exercised
 * through a full provider render is a boundary nobody tests exhaustively.
 */

/**
 * The origins Stripe serves hosted invoice pages from.
 *
 * 🔴 `hostedInvoiceUrl` is a BEARER CAPABILITY: the page it opens shows the
 * amount owed and the customer's billing identity, and it accepts payment
 * from anyone holding the link. The shipped `leadAgentInvoiceUrl` precedent
 * (`AlertCenter.hook.ts`) checks only `startsWith('https://')`, which is a
 * SCHEME check — it would happily send a paying customer to
 * `https://invoice.stripe.com.evil.example/...`. A payment destination gets
 * an origin allowlist instead.
 *
 * `invoice.stripe.com` is the current form of Stripe's `hosted_invoice_url`
 * (`/i/acct_<id>/<invoice>`); `pay.stripe.com` is the legacy form of the SAME
 * field. Both are Stripe-operated, and rejecting the legacy one would leave a
 * customer with an older open invoice unable to pay — the exact failure this
 * ladder exists to prevent. Anything else is rejected and logged.
 */
export const STRIPE_HOSTED_INVOICE_ORIGINS: readonly string[] = [
  'https://invoice.stripe.com',
  'https://pay.stripe.com',
];

/**
 * Why a supplied invoice URL was refused. `null` reason with a `null` url
 * means the field was simply ABSENT — the normal state of a past-due
 * workspace whose `openInvoiceSource` is `none` — which is not a rejection and
 * is not worth chasing.
 */
export type tPastDueInvoiceUrlRejection =
  | 'not-a-string'
  | 'empty'
  | 'unparseable'
  | 'has-credentials'
  | 'insecure-scheme'
  | 'has-port'
  | 'untrusted-origin';

export type tStripeInvoiceUrlVerdict = {
  /** The value, verbatim, when it passed every check. */
  url: string | null;
  reason: tPastDueInvoiceUrlRejection | null;
  /**
   * Hostname only — never the path or query. A rejected value can itself be a
   * live bearer link (a mis-mapped invoice, a hostile one), so the log gets a
   * chase-able breadcrumb and nothing that could be opened.
   */
  host: string | null;
};

/** The subscription fields the past-due slice reads, and only those. */
export type tPastDueSubscriptionFields = Pick<
  tSubscriptionSummary,
  | 'status'
  | 'currency'
  | 'daysPastDue'
  | 'amountRemaining'
  | 'cancellationDate'
  | 'nextPaymentAttempt'
  // The populated retry field under `send_invoice`, where Stripe never
  // auto-charges and leaves `nextPaymentAttempt` null (R1). The other two
  // past-due surfaces have always read both, in this order.
  | 'dueDate'
  | 'hostedInvoiceUrl'
  | 'attempted'
  | 'attemptCount'
  | 'collectionMethod'
  | 'declineCode'
  | 'cardBrand'
  | 'cardLast4'
>;

export type tPastDueSnapshotInput = {
  subscription: tPastDueSubscriptionFields | null | undefined;
  /**
   * `useSubscriptionContext().daysPastDue` — the RESOLVED counter (published
   * value, with U8's legacy fallback while the BFF field finishes rolling
   * out), NOT `subscription.daysPastDue`. Already computed: never re-derive it
   * from dates here, and never clamp it.
   */
  resolvedDaysPastDue: number | null;
  /** `VITE_PAST_DUE_LADDER_FEATURE`. Off = this whole slice is inert. */
  isLadderEnabled: boolean;
};

/**
 * The twelve snapshot fields, plus the two primitives the caller needs to log
 * a rejected invoice URL. `Pick`ed from `tAlertSnapshot` on purpose: renaming
 * a field there must break here, not drift.
 */
export type tPastDueSnapshotFields = Pick<
  tAlertSnapshot,
  | 'subscriptionPastDue'
  | 'daysPastDue'
  | 'pastDueAmount'
  | 'pastDueCancellationDate'
  | 'pastDueCancellationPassed'
  | 'pastDueInvoiceUrl'
  | 'pastDueNextPaymentAttempt'
  | 'pastDueAttempted'
  | 'pastDueAttemptCount'
  | 'pastDueCollectionMethod'
  | 'pastDueDeclineCode'
  | 'pastDueCardBrand'
  | 'pastDueCardLast4'
> & {
  invoiceUrlRejectionReason: tPastDueInvoiceUrlRejection | null;
  invoiceUrlRejectionHost: string | null;
};

const EMPTY_SLICE: tPastDueSnapshotFields = {
  subscriptionPastDue: false,
  daysPastDue: null,
  pastDueAmount: null,
  pastDueCancellationDate: null,
  pastDueCancellationPassed: false,
  pastDueInvoiceUrl: null,
  pastDueNextPaymentAttempt: null,
  pastDueAttempted: null,
  pastDueAttemptCount: null,
  pastDueCollectionMethod: null,
  pastDueDeclineCode: null,
  pastDueCardBrand: null,
  pastDueCardLast4: null,
  invoiceUrlRejectionReason: null,
  invoiceUrlRejectionHost: null,
};

/**
 * Parses a supplied `hosted_invoice_url` and accepts it ONLY when it is an
 * https URL on an approved Stripe origin, with no port and no embedded
 * credentials.
 *
 * ⚠️ The credential check runs BEFORE the origin check on purpose:
 * `https://attacker:pw@invoice.stripe.com/...` has origin
 * `https://invoice.stripe.com`, so an origin-only guard would wave it through.
 *
 * A non-default port is refused outright. The default port normalises away
 * (`new URL('https://host:443/x').port === ''`), which is the same origin and
 * therefore the same destination, so there is nothing left to refuse there.
 */
export function inspectStripeInvoiceUrl(
  raw: unknown,
): tStripeInvoiceUrlVerdict {
  if (raw === null || raw === undefined) {
    return { url: null, reason: null, host: null };
  }
  if (typeof raw !== 'string') {
    return { url: null, reason: 'not-a-string', host: null };
  }
  if (raw.trim() === '') {
    // The BFF nulls this field when it redacts it; an empty string reaching us
    // means something upstream sent `""` instead, and `""` survives a `??`
    // guard where `null` would not. Worth a line in the log.
    return { url: null, reason: 'empty', host: null };
  }

  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    return { url: null, reason: 'unparseable', host: null };
  }

  // `hostname` and not `host`: no port, and never the path or query.
  const host = parsed.hostname === '' ? null : parsed.hostname;

  if (parsed.username !== '' || parsed.password !== '') {
    return { url: null, reason: 'has-credentials', host };
  }
  if (parsed.protocol !== 'https:') {
    return { url: null, reason: 'insecure-scheme', host };
  }
  if (parsed.port !== '') {
    return { url: null, reason: 'has-port', host };
  }
  if (!STRIPE_HOSTED_INVOICE_ORIGINS.includes(parsed.origin)) {
    return { url: null, reason: 'untrusted-origin', host };
  }

  return { url: raw, reason: null, host };
}

/**
 * 🔴 PAST_DUE **or** NO_PAID.
 *
 * `SubscriptionStatus::mapStripeStatus` maps Stripe's `unpaid` to NO_PAID, so
 * a gate on PAST_DUE alone would darken the entire ladder the day somebody
 * flips Stripe's Revenue Recovery setting from "cancel" to "mark unpaid" —
 * silently, with no error anywhere. Deriving on both degrades to a LIT ladder
 * instead of silence, which is the failure direction a customer can act on.
 */
export function isPastDueStatus(
  status: eSubscriptionStatus | null | undefined,
): boolean {
  return (
    status === eSubscriptionStatus.PAST_DUE ||
    status === eSubscriptionStatus.NO_PAID
  );
}

/**
 * U12 (KTD1) — has the cancellation bound already gone by?
 *
 * Answered HERE, on the raw ISO value, because the snapshot publishes the date
 * as an already-formatted, localized display string and re-parsing "30 de
 * septiembre de 2026" would make a truth claim depend on the viewer's locale
 * spelling. PAST_DUE subscriptions exist at day 20 and day 60, so this branch
 * is reached in practice, not hypothetically.
 *
 * Unknown or unparseable degrades to `false`: the fail-safe direction is to
 * keep stating the bound we were given, not to go silent.
 */
export function hasCancellationBoundPassed(
  cancellationDate: string | null | undefined,
  now: number = Date.now(),
): boolean {
  if (typeof cancellationDate !== 'string' || cancellationDate.trim() === '') {
    return false;
  }
  const parsed = Date.parse(cancellationDate);
  if (Number.isNaN(parsed)) return false;
  return now > parsed;
}

export function resolvePastDueSnapshot(
  input: tPastDueSnapshotInput,
): tPastDueSnapshotFields {
  const { subscription, resolvedDaysPastDue, isLadderEnabled } = input;

  // Flag off = the ladder does not exist, so neither does its data. This is
  // the second of two independent gates (the declarations carry the first):
  // nothing threaded here can reach a customer before the flag flips, and the
  // legacy `endDate` derivation the resolved counter still falls back to while
  // the flag is off never lands in a snapshot.
  if (
    !isLadderEnabled ||
    !subscription ||
    !isPastDueStatus(subscription.status)
  ) {
    return EMPTY_SLICE;
  }

  // Published first, raw published second. `??` and never `||`: `0` is the
  // FIRST DAY of arrears, a real value, and `||` would read it as "no sé" and
  // fall through on exactly the day the counter starts to matter. The
  // context-level counter gates on PAST_DUE, so the raw field is what keeps a
  // NO_PAID workspace (the Stripe-setting case above) on a real rung.
  const days = resolvedDaysPastDue ?? subscription.daysPastDue ?? null;

  /*
   * 🔴 The SAME formatter as the tile and the Settings row, not the Lead Agent's.
   *
   * `formatInvoiceAmount` takes a locale and we were handing it `i18n.language`,
   * which production pins to bare `'es'` (Spain) — so a 1299900-cent MXN debt
   * rendered `12.999,00 MXN` in the bell panel and `$12,999.00 MXN` in Settings:
   * the separators INVERTED, on the one figure the customer is being asked to
   * pay, with the ladder's version printed directly above the pay button. A
   * Mexican reader parses `12.999,00` as twelve-point-nine-nine-nine.
   *
   * `formatPastDueDate`'s own docblock already names the tile, the Settings row
   * AND the ladder as its three consumers and says a customer comparing two
   * screens must not find two renderings of the same published field (U14). The
   * ladder was the one surface not honouring that. It does now.
   *
   * This also fixes a second bug for free: the old guard was `Number.isFinite`,
   * so `amountRemaining: 0` formatted as `$0.00` and the rung announced a debt
   * of nothing. `formatAmountOwed` returns null at or below zero — the same
   * reason bff-web redacts to null rather than to 0.
   */
  const pastDueAmount = formatAmountOwed({
    amountMinorUnits: subscription.amountRemaining,
    currency: subscription.currency,
  });

  const invoiceUrl = inspectStripeInvoiceUrl(subscription.hostedInvoiceUrl);

  /*
   * The cancellation bound keeps an ELAPSED date on purpose — once it passes,
   * the rung goes dateless via `pastDueCancellationPassed`, and it cannot make
   * that call if the date has already been nulled from under it. It still
   * refuses the `0001-01-01` zero-value sentinel.
   *
   * 🔴 The retry date gets the full guard, which it previously had NONE of
   * while the same field got it on the other two surfaces. A Go zero time
   * parses as valid and formatted as "1 de enero de 0001", and the string is
   * truthy, so the wide strip announced "el próximo intento de cobro es el 1 de
   * enero de 0001" while Settings — reading the same field through
   * `resolveRetryDate` — rendered nothing. An elapsed value was worse: the two
   * screens contradicted each other with a date three weeks gone.
   *
   * `resolveRetryDate` also brings the `due_date` fallback, which is the
   * populated field under `send_invoice` where Stripe never auto-charges — so
   * manual-collection workspaces stop losing the date entirely.
   */
  const cancellationBound = resolvePlausibleDate({
    value: subscription.cancellationDate,
  });
  const retryDate = resolveRetryDate({
    fields: {
      nextPaymentAttempt: subscription.nextPaymentAttempt,
      dueDate: subscription.dueDate,
    },
  });

  return {
    subscriptionPastDue: true,
    daysPastDue: days,
    pastDueAmount,
    pastDueCancellationDate: cancellationBound
      ? formatPastDueDate(cancellationBound)
      : null,
    pastDueCancellationPassed: hasCancellationBoundPassed(
      subscription.cancellationDate,
    ),
    pastDueInvoiceUrl: invoiceUrl.url,
    pastDueNextPaymentAttempt: retryDate ? formatPastDueDate(retryDate) : null,
    pastDueAttempted: subscription.attempted ?? null,
    pastDueAttemptCount: subscription.attemptCount ?? null,
    pastDueCollectionMethod: subscription.collectionMethod ?? null,
    pastDueDeclineCode: subscription.declineCode ?? null,
    pastDueCardBrand: subscription.cardBrand ?? null,
    pastDueCardLast4: subscription.cardLast4 ?? null,
    invoiceUrlRejectionReason: invoiceUrl.reason,
    invoiceUrlRejectionHost: invoiceUrl.host,
  };
}
